import { Component, OnInit, Inject, DestroyRef, HostListener, inject } from "@angular/core";
import { NgOptimizedImage } from "@angular/common";
import {
  MsalService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from "@azure/msal-angular";
import {
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import { ActivatedRoute } from "@angular/router";
import { RegisterInvitedUserService } from "../../data/util/register-invited-user.service";
import { Router } from "@angular/router";
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { GetUseritselfService } from "../../data/user/get-useritself.service";
import {
  FormGroup,
  FormControl,
  FormGroupDirective,
  NgForm,
  Validators,
} from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import { MatSnackBar } from "@angular/material/snack-bar";

import { ConfirmDialogComponent } from "../../shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";

import { DatabaseCacheService } from "../../data/cache/database-cache.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { JOIN_SECTION_I18N } from "./join-section.i18n";
import { environment } from "../../../environments/environment";
import { getRedirectUri } from "../../utils";

@Component({
  selector: "app-join-section",
  templateUrl: "./join-section.component.html",
  styleUrls: ["./join-section.component.css"],
  imports: [...SHARED_IMPORTS, NgOptimizedImage],
})
export class JoinSectionComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly joinSectionPendingLoginKey = "gokuri.joinSection.pendingLogin";

  loginDisplay = false;

  joinSectionForm: FormGroup;

  user;
  isLoggedIn;
  key;
  hide = true;

  isLinkValid = true;
  invalidMessage = "";

  waitingForResult = false;

  /** Debug mode — enabled only in non-production when key is "__debug__" */
  debugMode = false;
  debugPanelCollapsed = false;

  // Input value validations
  signInNamesFormControl = new FormControl("", [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(32),
    Validators.pattern("^[a-zA-Z][a-zA-Z0-9\-]*$"),
  ]);

  loginPasswordFormControl = new FormControl("", [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(64),
    Validators.pattern(
      "^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z])(?=.*[!-/:-@¥[-`{-~+])[a-zA-Z0-9!-/:-@¥[-`{-~+]{0,100}$",
    ),
  ]);

  lastNameFormControl = new FormControl("", [Validators.required]);

  firstNameFormControl = new FormControl("", [Validators.required]);

  // lastNameENFormControl = new FormControl("", [
  //   Validators.required,
  //   Validators.pattern("[a-zA-Z ]*"),
  // ]);

  // firstNameENFormControl = new FormControl("", [
  //   Validators.pattern("[a-zA-Z ]*"),
  // ]);

  contractStartAtFormControl = new FormControl("", [
    Validators.pattern(
      "^((19|2[0-9])[0-9]{2})/(0[1-9]|1[012])/(0[1-9]|[12][0-9]|3[01])$",
    ),
  ]);

  matcher = new MyErrorStateMatcher();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private route: ActivatedRoute,
    private registerInvitedUserService: RegisterInvitedUserService,
    private getUseritselfService: GetUseritselfService,
    private router: Router,
    private storeAuth: StoreAuthService,
    private snackBar: MatSnackBar,
    public _matdialog: MatDialog,
    public common: DatabaseCacheService,
  ) {}

  ngOnInit() {
    this.setLoginDisplay();

    this.joinSectionForm = new FormGroup({
      signInNames: this.signInNamesFormControl,
      loginPassword: this.loginPasswordFormControl,
      lastName: this.lastNameFormControl,
      firstName: this.firstNameFormControl,
      // lastNameEN: this.lastNameENFormControl,
      // firstNameEN: this.firstNameENFormControl,
    });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (params) => {
      const key = params["key"];
      if (key == null) {
        this.router.navigate(["/not-found"]);
      } else if (key === "__debug__" && !environment.production) {
        this.debugMode = true;
        this.key = "__debug__";
      } else {
        this.key = encodeURIComponent(key);
        this.tryAutoRegisterExistingUser();
      }
    });
  }

  @HostListener("window:pageshow")
  handlePageShow(): void {
    // Browser back from Azure AD can restore this page from bfcache with stale disabled state.
    this.waitingForResult = false;
    this.tryAutoRegisterExistingUser();
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  /** Check if the error indicates the invitation link is no longer usable and block further submission. */
  private markLinkInvalidIfApplicable(errorMessage: string): boolean {
    const msg = (errorMessage || "").toLowerCase();
    if (msg.includes("invalid") || msg.includes("not found")) {
      this.isLinkValid = false;
      this.invalidMessage = JOIN_SECTION_I18N.message.linkInvalid;
      return true;
    }
    if (msg.includes("expired")) {
      this.isLinkValid = false;
      this.invalidMessage = JOIN_SECTION_I18N.message.linkExpired;
      return true;
    }
    if (msg.includes("cancel")) {
      this.isLinkValid = false;
      this.invalidMessage = JOIN_SECTION_I18N.message.linkCanceled;
      return true;
    }
    return false;
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    this.waitingForResult = true;
    this.markJoinSectionLoginPending();
    const redirectUri = getRedirectUri();

    let joinSectionAfterLoginParams = {
      key: this.key,
    };

    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
        ...userFlowRequest,
        redirectUri,
        state: JSON.stringify({
          callback: "join-section",
          data: joinSectionAfterLoginParams,
        }),
      } as RedirectRequest); //loginHint: '' domainHint: ''
    } else {
      this.authService.loginRedirect({
        ...(userFlowRequest ?? {}),
        redirectUri,
      } as RedirectRequest);
    }
  }

  private markJoinSectionLoginPending(): void {
    if (!this.key) {
      return;
    }

    sessionStorage.setItem(
      this.joinSectionPendingLoginKey,
      JSON.stringify({ key: this.key, timestamp: Date.now() }),
    );
  }

  private shouldAutoRegisterExistingUser(): boolean {
    const marker = sessionStorage.getItem(this.joinSectionPendingLoginKey);
    if (!marker || !this.key || !this.isLinkValid) {
      return false;
    }

    try {
      const parsed = JSON.parse(marker) as { key?: string; timestamp?: number };
      if (parsed?.key !== this.key) {
        return false;
      }

      const timestamp = parsed?.timestamp ?? 0;
      const isFresh = Date.now() - timestamp < 5 * 60 * 1000;
      return isFresh;
    } catch {
      return false;
    }
  }

  private tryAutoRegisterExistingUser(): void {
    this.setLoginDisplay();

    if (!this.loginDisplay || this.waitingForResult) {
      return;
    }

    if (!this.shouldAutoRegisterExistingUser()) {
      return;
    }

    sessionStorage.removeItem(this.joinSectionPendingLoginKey);
    this.registerExistingUser();
  }

  registerNewUser(joinSectionFormValue) {
    this.waitingForResult = true;

    joinSectionFormValue.createUser = true;

    this.registerInvitedUserService
      .registerInvitedUser(this.key, joinSectionFormValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {

          let error = (res as any).error;

          if (error != null) {
            console.error("ERROR...", error.code, error.message);

            if (
              error.code === "E0000xx" &&
              error.message.includes("userPrincipalName already exists")
            ) {
              this._matdialog.open(ConfirmDialogComponent, {
                disableClose: false,
                data: {
                  title: JOIN_SECTION_I18N.dialog.titleWarning,
                  message: JOIN_SECTION_I18N.message.userIdAlreadyTaken,
                  hideNoButton: true,
                  confirmButtonText: JOIN_SECTION_I18N.button.ok,
                },
              });
            } else {
              this.markLinkInvalidIfApplicable(error.message);
              this.snackBar.open(
                JOIN_SECTION_I18N.message.createUserFailed +
                  (error.message || JSON.stringify(error)),
                JOIN_SECTION_I18N.button.close,
                { duration: 10000 },
              );
            }
          } else {
            this.router.navigate(["/join-section-confirmed"]);
          }

          this.waitingForResult = false;
        },
        (httpError) => {
          console.error("ERROR...", httpError);

          this.snackBar.open(
            JOIN_SECTION_I18N.message.createUserFailed + httpError,
            JOIN_SECTION_I18N.button.close,
            { duration: 10000 }
          );

          this.waitingForResult = false;
        }
      )
  }
    

  public submit = (joinSectionFormValue) => {

    if (this.joinSectionForm.valid) {
      this.registerNewUser(joinSectionFormValue);
    }
  };

  registerExistingUser() {

    this.waitingForResult = true;

    let account = this.authService.instance.getActiveAccount();

    // Fallback to a usable cached account when active account is missing.
    // This can happen after redirect/cross-tab transitions.
    if (!account) {
      const cachedAccounts = this.authService.instance.getAllAccounts();
      const usableAccount = cachedAccounts.find(
        (a) => !!a?.idTokenClaims?.["sub"],
      );
      if (usableAccount) {
        this.authService.instance.setActiveAccount(usableAccount);
        account = usableAccount;
      }
    }

    const loginUserId = account?.idTokenClaims?.["sub"];
    if (!loginUserId) {
      this.login();
      return;
    }

    let bodyRequest = {
      userId: loginUserId,
      createUser: false,
    };

    this.registerInvitedUserService
      .registerInvitedUser(this.key, bodyRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.waitingForResult = false;

          let error = (res as any).error;

          if (error != null) {
            console.error("ERROR...", error.code, error.message);

            if (
              error.code === "E0000xx" &&
              error.message.includes("user already belongs")
            ) {
              const sectionName = error.data;
              this._matdialog.open(ConfirmDialogComponent, {
                disableClose: false,
                data: {
                  title: JOIN_SECTION_I18N.dialog.titleWarning,
                  message: JOIN_SECTION_I18N.message.alreadyBelongs(sectionName),
                  hideNoButton: true,
                  confirmButtonText: JOIN_SECTION_I18N.button.ok,
                },
              });
            } else {
              this.markLinkInvalidIfApplicable(error.message);
              this.snackBar.open(
                JOIN_SECTION_I18N.message.addPermissionFailed +
                  (error.message || JSON.stringify(error)),
                JOIN_SECTION_I18N.button.close,
                { duration: 10000 },
              );
            }
          } else {
            let message = "";

            if ((res as any).belongs?.sectionInfo?.sectionName) {
              message = JOIN_SECTION_I18N.message.sectionAccess((res as any).belongs?.sectionInfo?.sectionName);
            } else if ((res as any).type == "sectionGroupAdmin") {
              message = JOIN_SECTION_I18N.message.sectionGroupAdminAccess;
            } else if ((res as any).type == "systemAdmin") {
              message = JOIN_SECTION_I18N.message.systemAdminAccess;
            }

            const dialogRef = this._matdialog.open(ConfirmDialogComponent, {
              disableClose: true,
              data: {
                title: JOIN_SECTION_I18N.dialog.titleJoinComplete,
                message: message,
                hideNoButton: true,
                confirmButtonText: JOIN_SECTION_I18N.button.ok,
              },
            });

            dialogRef.afterClosed().subscribe((result) => {

              if (result) {
                this.getUseritself((res as any).sectionId);
              }
            });
          }
        },
        (error) => {
          console.error("ERROR...", error);

          this.snackBar.open(
            JOIN_SECTION_I18N.message.addPermissionFailed +
              JSON.stringify(error.error),
            JOIN_SECTION_I18N.button.close,
            {
              duration: 10000,
            },
          );
          this.waitingForResult = false;
        },
      );
  }

  async getUseritself(sectionId) {
    try {
      let responseData = await this.common.setLoginUserAndSelectedSection();

      if (responseData.sectionWithRole.length > 0) {
        const firstSection = {
          sectionId: responseData.sectionWithRole[0].sectionId,
          sectionName: responseData.sectionWithRole[0].sectionName,
        };

        this.common.setSelectedSection(firstSection);

        this.router.navigate(["home", firstSection.sectionId, "users"]);
      } else {
        let systemAdmin = responseData.systemAdminAssignmentDocument;
        let sectionGroupAdmins =
          responseData.sectionGroupAdminAssignmentDocuments;

        if (systemAdmin != null) {
          this.router.navigate(["system-admin", "section-groups"]);
        } else if (sectionGroupAdmins.length > 0) {
          let sectionGroupId = sectionGroupAdmins[0].sectionGroupId;
          this.router.navigate([
            "section-group-admin",
            sectionGroupId,
            "sections",
          ]);
        }
      }

      // this.spinnerService.hide();
      // //this.router.navigate(['/home']);
      // const currentLocale = this.route.snapshot.paramMap.get('lang');
      // this.router.navigate(['home', sectionId, 'users']);
    } catch {
      this.common.resetSelectedSection();
    }
  }

  // ── Debug scenario triggers (non-production only) ──

  debugSimulateNewUserSuccess(): void {
    this.router.navigate(["/join-section-confirmed"]);
  }

  debugSimulateExistingUserSuccess(): void {
    this._matdialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: {
        title: JOIN_SECTION_I18N.dialog.titleJoinComplete,
        message: JOIN_SECTION_I18N.message.sectionAccess("デバッグセクション"),
        hideNoButton: true,
        confirmButtonText: JOIN_SECTION_I18N.button.ok,
      },
    });
  }

  debugSimulateSectionGroupAdminSuccess(): void {
    this._matdialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: {
        title: JOIN_SECTION_I18N.dialog.titleJoinComplete,
        message: JOIN_SECTION_I18N.message.sectionGroupAdminAccess,
        hideNoButton: true,
        confirmButtonText: JOIN_SECTION_I18N.button.ok,
      },
    });
  }

  debugSimulateSystemAdminSuccess(): void {
    this._matdialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: {
        title: JOIN_SECTION_I18N.dialog.titleJoinComplete,
        message: JOIN_SECTION_I18N.message.systemAdminAccess,
        hideNoButton: true,
        confirmButtonText: JOIN_SECTION_I18N.button.ok,
      },
    });
  }

  debugSimulateDuplicateUserId(): void {
    this._matdialog.open(ConfirmDialogComponent, {
      disableClose: false,
      data: {
        title: JOIN_SECTION_I18N.dialog.titleWarning,
        message: JOIN_SECTION_I18N.message.userIdAlreadyTaken,
        hideNoButton: true,
        confirmButtonText: JOIN_SECTION_I18N.button.ok,
      },
    });
  }

  debugSimulateAlreadyBelongs(): void {
    this._matdialog.open(ConfirmDialogComponent, {
      disableClose: false,
      data: {
        title: JOIN_SECTION_I18N.dialog.titleWarning,
        message: JOIN_SECTION_I18N.message.alreadyBelongs("デバッグセクション"),
        hideNoButton: true,
        confirmButtonText: JOIN_SECTION_I18N.button.ok,
      },
    });
  }

  debugSimulateLinkExpired(): void {
    this.isLinkValid = false;
    this.invalidMessage = JOIN_SECTION_I18N.message.linkExpired;
  }

  debugSimulateLinkInvalid(): void {
    this.isLinkValid = false;
    this.invalidMessage = JOIN_SECTION_I18N.message.linkInvalid;
  }

  debugSimulateLinkCanceled(): void {
    this.isLinkValid = false;
    this.invalidMessage = JOIN_SECTION_I18N.message.linkCanceled;
  }

  debugSimulateUnknownError(): void {
    this.snackBar.open(
      JOIN_SECTION_I18N.message.addPermissionFailed + "Unknown server error (debug)",
      JOIN_SECTION_I18N.button.close,
      { duration: 10000 },
    );
  }

  debugReset(): void {
    this.isLinkValid = true;
    this.invalidMessage = "";
    this.waitingForResult = false;
  }
}

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}
